/* Editable project screen drafts: lightweight semantic drawing canvas. */
(function () {
  const { useEffect, useMemo, useRef, useState } = React;

  const HISTORY_LIMIT = 80;

  const TOOLS = [
    { id: 'select', label: 'Select', icon: 'arrow' },
    { id: 'pen', label: 'Pen', icon: 'edit' },
    { id: 'text', label: 'Text', icon: 'note' },
    { id: 'eraser', label: 'Eraser', icon: 'trash' },
    { id: 'hotspot', label: 'Hotspot', icon: 'link' }
  ];

  function ProjectScreenDraftPanel({ project, active, screenDrafts, onAction, onZenChange, onActiveDraftChange }) {
    const scopedDrafts = useMemo(() => {
      const activeId = active && active.id;
      return (screenDrafts || [])
        .filter((item) => !activeId || !item.workPackageId || item.workPackageId === activeId)
        .slice()
        .sort(sortPositionTitle);
    }, [screenDrafts, active && active.id]);
    const [selectedDraftId, setSelectedDraftId] = useState(() => scopedDrafts[0] && scopedDrafts[0].id || '');
    const selectedDraft = scopedDrafts.find((item) => item.id === selectedDraftId) || null;
    const [draft, setDraft] = useState(() => parseDraft(selectedDraft, project, active));
    const [draftSource, setDraftSource] = useState(() => draftSignature(selectedDraft));
    const [tool, setTool] = useState('pen');
    const [selectedElementId, setSelectedElementId] = useState('');
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [draftMenuOpen, setDraftMenuOpen] = useState(false);
    const [draftHistory, setDraftHistory] = useState({ undo: [], redo: [] });
    const lastSavedRef = useRef(stringifyDraft(draft));
    const draftCacheRef = useRef({});

    useEffect(() => {
      setSelectedDraftId((current) => {
        if (current && scopedDrafts.some((item) => item.id === current)) return current;
        return scopedDrafts[0] && scopedDrafts[0].id || '';
      });
    }, [scopedDrafts.map((item) => item.id).join('|')]);

    useEffect(() => {
      const source = draftSignature(selectedDraft);
      const cache = selectedDraft ? draftCacheRef.current[selectedDraft.id] : null;
      const next = cache && cache.source === source ? cache.draft : parseDraft(selectedDraft, project, active);
      const saved = stringifyDraft(parseDraft(selectedDraft, project, active));
      setDraft(next);
      setDraftSource(source);
      setDirty(stringifyDraft(next) !== saved);
      setSelectedElementId('');
      setDraftMenuOpen(false);
      setDraftHistory({ undo: [], redo: [] });
      lastSavedRef.current = saved;
    }, [selectedDraft && selectedDraft.id, selectedDraft && recordJson(selectedDraft), active && active.id]);

    useEffect(() => {
      if (!fullscreen) return;
      const onKey = (event) => {
        if (event.key === 'Escape') setFullscreen(false);
      };
      document.body.classList.add('screen-draft-fullscreen-open');
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.classList.remove('screen-draft-fullscreen-open');
        window.removeEventListener('keydown', onKey);
      };
    }, [fullscreen]);

    useEffect(() => {
      if (onZenChange) onZenChange(fullscreen);
      return () => { if (onZenChange) onZenChange(false); };
    }, [fullscreen]);

    useEffect(() => {
      if (!onActiveDraftChange) return;
      onActiveDraftChange(selectedDraft ? {
        kind: 'screenDraft',
        projectName: project && project.name || '',
        draftName: selectedDraft.title || 'Screen draft'
      } : null);
      return () => onActiveDraftChange(null);
    }, [onActiveDraftChange, project && project.name, selectedDraft && selectedDraft.id, selectedDraft && selectedDraft.title]);

    const elementCount = (draft.elements || []).length;
    const linkCount = (draft.links || []).length;
    const hasUnsavedChanges = dirty;
    const canUndo = draftHistory.undo.length > 0;
    const canRedo = draftHistory.redo.length > 0;

    const cacheDraft = (nextDraft) => {
      if (!selectedDraft) return;
      draftCacheRef.current[selectedDraft.id] = {
        source: draftSignature(selectedDraft),
        draft: nextDraft
      };
    };

    const applyDraftSnapshot = (snapshot) => {
      const restored = parseDraftSnapshot(snapshot, project, active, selectedDraft);
      setDraft(restored);
      cacheDraft(restored);
      setDirty(stringifyDraft(restored) !== lastSavedRef.current);
      setSelectedElementId('');
    };

    const updateDraft = (nextDraft) => {
      const normalized = normalizeDraft(nextDraft, project, active);
      const previousSnapshot = stringifyDraft(draft);
      const nextSnapshot = stringifyDraft(normalized);
      if (nextSnapshot !== previousSnapshot) {
        setDraftHistory((current) => ({
          undo: pushHistorySnapshot(current.undo, previousSnapshot),
          redo: []
        }));
      }
      setDraft(normalized);
      cacheDraft(normalized);
      setDirty(nextSnapshot !== lastSavedRef.current);
    };

    const undoDraft = () => {
      if (!canUndo || saving) return;
      const previousSnapshot = draftHistory.undo[draftHistory.undo.length - 1];
      const currentSnapshot = stringifyDraft(draft);
      applyDraftSnapshot(previousSnapshot);
      setDraftHistory({
        undo: draftHistory.undo.slice(0, -1),
        redo: [currentSnapshot].concat(draftHistory.redo).slice(0, HISTORY_LIMIT)
      });
    };

    const redoDraft = () => {
      if (!canRedo || saving) return;
      const nextSnapshot = draftHistory.redo[0];
      const currentSnapshot = stringifyDraft(draft);
      applyDraftSnapshot(nextSnapshot);
      setDraftHistory({
        undo: pushHistorySnapshot(draftHistory.undo, currentSnapshot),
        redo: draftHistory.redo.slice(1)
      });
    };

    useEffect(() => {
      const onKey = (event) => {
        if (!selectedDraft || saving || isEditableTarget(event.target)) return;
        const modifier = event.metaKey || event.ctrlKey;
        if (!modifier) return;
        const key = String(event.key || '').toLowerCase();
        if (key === 'z') {
          event.preventDefault();
          if (event.shiftKey) redoDraft();
          else undoDraft();
        } else if (key === 'y') {
          event.preventDefault();
          redoDraft();
        }
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [selectedDraft && selectedDraft.id, saving, draft, draftHistory]);

    const save = async (nextDraft = draft, options = {}) => {
      if (!selectedDraft || saving) return false;
      setSaving(true);
      try {
        const ok = await onAction('updateProjectScreenDraft', {
          projectId: project.id,
          screenDraftId: selectedDraft.id,
          screenDraft: draftBody(selectedDraft, nextDraft, active),
          silent: options.silent !== undefined ? options.silent : true
        });
        if (ok !== false) {
          const saved = stringifyDraft(nextDraft);
          lastSavedRef.current = saved;
          delete draftCacheRef.current[selectedDraft.id];
          setDirty(false);
        }
        return ok !== false;
      } finally {
        setSaving(false);
      }
    };

    const saveIfNeeded = async () => {
      if (!selectedDraft || !hasUnsavedChanges) return true;
      return save(draft, { silent: true });
    };

    const selectDraft = async (draftId) => {
      if (!draftId || draftId === selectedDraftId || saving) return;
      const saved = await saveIfNeeded();
      if (saved === false) return;
      setSelectedDraftId(draftId);
      setDraftMenuOpen(false);
    };

    const createDraft = () => onAction('createProjectScreenDraft', {
      projectId: project.id,
      screenDraft: draftBody(null, defaultScreenDraft(project, active), active)
    });

    const deleteDraft = (item) => {
      if (!item) return;
      if (!window.confirm(`Delete "${item.title || 'Screen draft'}"? This removes the draft canvas and hotspots.`)) return;
      onAction('deleteProjectScreenDraft', { projectId: project.id, screenDraftId: item.id, title: item.title || 'this screen draft' });
    };

    const renameDraft = async (item = selectedDraft) => {
      if (!item || saving) return;
      const nextTitle = cleanText(window.prompt('Screen draft name', item.title || 'Screen draft'));
      if (!nextTitle || nextTitle === (item.title || '')) return;
      const itemDraft = item.id === selectedDraftId ? draft : parseDraft(item, project, active);
      const ok = await onAction('updateProjectScreenDraft', {
        projectId: project.id,
        screenDraftId: item.id,
        screenDraft: draftBody(item, Object.assign({}, itemDraft, { title: nextTitle }), active, { title: nextTitle }),
        silent: false
      });
      if (ok !== false && item.id === selectedDraftId) {
        const nextDraft = Object.assign({}, itemDraft, { title: nextTitle });
        const saved = stringifyDraft(nextDraft);
        setDraft(nextDraft);
        cacheDraft(nextDraft);
        lastSavedRef.current = saved;
        setDirty(false);
        setDraftHistory({ undo: [], redo: [] });
      }
    };

    const sceneKey = selectedDraft ? `${selectedDraft.id}:${selectedDraft.updatedAt || recordJson(selectedDraft) || ''}` : 'empty';
    const selectedSource = draftSignature(selectedDraft);
    const canvasDraft = selectedDraft && draftSource === selectedSource ? draft : parseDraft(selectedDraft, project, active);

    return (
      <div className={'sketch-studio screen-draft-studio' + (fullscreen ? ' zen' : '')}>
        {!fullscreen && (
          <div className="sketch-studio-head screen-draft-head">
            <div className="sketch-picker sketch-head-picker">
              <button className="sketch-picker-btn" disabled={!scopedDrafts.length} onClick={() => setDraftMenuOpen((value) => !value)}>
                <span className="sketch-picker-ico"><Icon name="flow" size={14} /></span>
                <span>{selectedDraft ? selectedDraft.title || 'Screen draft' : 'No screen drafts'}</span>
                <Icon name="chevD" size={12} />
              </button>
              {draftMenuOpen && (
                <div className="sketch-picker-menu screen-draft-picker-menu">
                  {scopedDrafts.length ? scopedDrafts.map((item, index) => (
                    <div
                      key={item.id}
                      className={'sketch-picker-row ' + (selectedDraft && item.id === selectedDraft.id ? 'on' : '')}
                    >
                      <button type="button" className="sketch-picker-row-main" onClick={() => selectDraft(item.id)}>
                        <span className="dot" />
                        <span className="copy"><strong>{item.title || 'Screen draft'}</strong><small>{draftMetaLabel(item)}</small></span>
                        {selectedDraft && item.id === selectedDraft.id ? <Icon name="check" size={13} /> : null}
                      </button>
                      <span className="row-actions">
                        <button type="button" title="Move screen draft up" disabled={index <= 0} onClick={() => onAction('reorderProjectScreenDraft', { project, screenDraftId: item.id, direction: -1 })}><Icon name="arrowUp" size={12} /></button>
                        <button type="button" title="Move screen draft down" disabled={index === scopedDrafts.length - 1} onClick={() => onAction('reorderProjectScreenDraft', { project, screenDraftId: item.id, direction: 1 })}><Icon name="arrowDown" size={12} /></button>
                        <button type="button" title="Rename screen draft" onClick={() => renameDraft(item)}><Icon name="edit" size={12} /></button>
                        <button type="button" title="Delete screen draft" onClick={() => deleteDraft(item)}><Icon name="trash" size={12} /></button>
                      </span>
                    </div>
                  )) : <div className="sketch-picker-empty">No screen drafts yet.</div>}
                  <button className="sketch-picker-new" onClick={createDraft}><Icon name="plus" size={13} />New screen draft</button>
                </div>
              )}
            </div>
            <div className="sketch-studio-title">
              <h2>Screen drafts - {active && active.name || project.name || 'Work package'}</h2>
              <div className="sub">Semantic canvas, text, and hotspots scoped to the active work package</div>
            </div>
            <div className="actions">
              {hasUnsavedChanges ? <span className="chip">Unsaved</span> : <span className="chip">Saved</span>}
              <button className="btn sm ghost" onClick={() => setFullscreen(true)}><Icon name="maximize" size={13} />Full screen</button>
              <button className="btn sm ghost" disabled={!selectedDraft || saving} onClick={() => renameDraft()}><Icon name="edit" size={13} />Rename</button>
              <button className="btn sm ghost" disabled={!selectedDraft || saving} onClick={() => save(draft, { silent: false })}><Icon name="check" size={13} />{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        )}

        <div className="sketch-studio-workspace screen-draft-workspace">
          {selectedDraft ? (
            <React.Fragment>
              <div className="screen-draft-toolbar" role="toolbar" aria-label="Screen draft tools">
                <button
                  type="button"
                  className="btn sm ghost screen-draft-tool"
                  title="Undo"
                  disabled={!canUndo || saving}
                  onClick={undoDraft}
                >
                  <Icon name="undo" size={13} />
                  Undo
                </button>
                <button
                  type="button"
                  className="btn sm ghost screen-draft-tool"
                  title="Redo"
                  disabled={!canRedo || saving}
                  onClick={redoDraft}
                >
                  <Icon name="redo" size={13} />
                  Redo
                </button>
                <span className="screen-draft-toolbar-divider" aria-hidden="true" />
                {TOOLS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={'btn sm ghost screen-draft-tool' + (tool === item.id ? ' primary' : '')}
                    title={item.label}
                    aria-pressed={tool === item.id}
                    onClick={() => setTool(item.id)}
                  >
                    <Icon name={item.icon} size={13} />
                    {item.label}
                  </button>
                ))}
                <span className="chip">{elementCount} element{elementCount === 1 ? '' : 's'}</span>
                <span className="chip">{linkCount} hotspot{linkCount === 1 ? '' : 's'}</span>
              </div>
              <ScreenDraftCanvas
                key={sceneKey}
                draft={canvasDraft}
                drafts={scopedDrafts}
                tool={tool}
                selectedElementId={selectedElementId}
                onSelect={setSelectedElementId}
                onChange={updateDraft}
              />
              {fullscreen && <button className="sketch-exit-zen screen-draft-exit-zen" onClick={() => setFullscreen(false)}><Icon name="minimize" size={13} />Exit full screen</button>}
            </React.Fragment>
          ) : (
            <div className="sketch-empty-state screen-draft-empty-state">
              <span className="muted">Create a screen draft to sketch UI states, copy, and clickable hotspots.</span>
              <button className="btn sm" onClick={createDraft}><Icon name="plus" size={13} />New screen draft</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function ScreenDraftCanvas({ draft, drafts, tool, selectedElementId, onSelect, onChange }) {
    const svgRef = useRef(null);
    const pointerRef = useRef(null);
    const [preview, setPreview] = useState(null);
    const canvas = draft.canvas || {};
    const width = Number(canvas.width) || 1440;
    const height = Number(canvas.height) || 900;
    const background = canvas.background || '#ffffff';
    const gridSize = Number(canvas.gridSize) || 24;

    const commit = (patch) => {
      onChange(Object.assign({}, draft, patch));
    };

    const onPointerDown = (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      const point = eventPoint(event, svgRef.current);
      const hit = hitTestDraft(draft, point);
      if (svgRef.current && svgRef.current.setPointerCapture) svgRef.current.setPointerCapture(event.pointerId);

      if (tool === 'select') {
        if (!hit) {
          onSelect('');
          pointerRef.current = null;
          return;
        }
        onSelect(hit.id);
        pointerRef.current = {
          type: 'move',
          pointerId: event.pointerId,
          id: hit.id,
          start: point,
          original: cloneElement(hit.element),
          link: !!hit.link
        };
        return;
      }

      if (tool === 'eraser') {
        if (hit) {
          const next = removeElementOrLink(draft, hit.id);
          onSelect('');
          onChange(next);
        }
        pointerRef.current = null;
        return;
      }

      if (tool === 'text') {
        const text = window.prompt('Text');
        const clean = cleanText(text);
        if (clean) {
          const nextElement = {
            id: makeId('txt'),
            type: 'text',
            x: round(point.x),
            y: round(point.y),
            text: clean,
            fontSize: 24,
            color: '#182033',
            width: estimateTextWidth(clean, 24)
          };
          commit({ elements: (draft.elements || []).concat([nextElement]) });
          onSelect(nextElement.id);
        }
        pointerRef.current = null;
        return;
      }

      if (tool === 'hotspot') {
        pointerRef.current = {
          type: 'hotspot',
          pointerId: event.pointerId,
          start: point,
          current: point
        };
        setPreview({ type: 'hotspot', bounds: rectFromPoints(point, point) });
        return;
      }

      pointerRef.current = {
        type: 'pen',
        pointerId: event.pointerId,
        points: [point]
      };
      setPreview({ type: 'pen', points: [point] });
    };

    const onPointerMove = (event) => {
      const pointer = pointerRef.current;
      if (!pointer || pointer.pointerId !== event.pointerId) return;
      const point = eventPoint(event, svgRef.current);

      if (pointer.type === 'move') {
        const dx = point.x - pointer.start.x;
        const dy = point.y - pointer.start.y;
        const moved = moveElement(pointer.original, dx, dy);
        pointer.current = moved;
        setPreview({ type: 'move', id: pointer.id, element: moved, link: pointer.link });
        return;
      }

      if (pointer.type === 'hotspot') {
        pointer.current = point;
        setPreview({ type: 'hotspot', bounds: rectFromPoints(pointer.start, point) });
        return;
      }

      if (pointer.type === 'pen') {
        const last = pointer.points[pointer.points.length - 1];
        if (!last || distance(last, point) >= 1.5) pointer.points.push(point);
        setPreview({ type: 'pen', points: pointer.points });
      }
    };

    const onPointerUp = (event) => {
      const pointer = pointerRef.current;
      if (!pointer || pointer.pointerId !== event.pointerId) return;
      if (svgRef.current && svgRef.current.releasePointerCapture) {
        try { svgRef.current.releasePointerCapture(event.pointerId); } catch (_) {}
      }
      pointerRef.current = null;
      setPreview(null);

      if (pointer.type === 'move') {
        const point = eventPoint(event, svgRef.current);
        const dx = point.x - pointer.start.x;
        const dy = point.y - pointer.start.y;
        const current = moveElement(pointer.original, dx, dy);
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
          onChange(replaceElementOrLink(draft, pointer.id, current));
        }
        return;
      }

      if (pointer.type === 'hotspot') {
        pointer.current = eventPoint(event, svgRef.current);
        const bounds = normalizeBounds(rectFromPoints(pointer.start, pointer.current || pointer.start));
        if (bounds.width < 8 || bounds.height < 8) return;
        const label = cleanText(window.prompt('Hotspot label', 'Open screen'));
        if (!label) return;
        const targetScreenDraftId = chooseTargetDraft(drafts, draft.id);
        const nextLink = {
          id: makeId('hot'),
          type: 'hotspot',
          bounds,
          label,
          targetScreenDraftId
        };
        commit({ links: (draft.links || []).concat([nextLink]) });
        onSelect(nextLink.id);
        return;
      }

      if (pointer.type === 'pen') {
        const point = eventPoint(event, svgRef.current);
        const last = pointer.points[pointer.points.length - 1];
        if (!last || distance(last, point) >= 1.5) pointer.points.push(point);
        const element = normalizeStroke(pointer.points || []);
        if (!element) return;
        commit({ elements: (draft.elements || []).concat([element]) });
        onSelect(element.id);
      }
    };

    const renderedElements = preview && preview.type === 'move' && !preview.link
      ? (draft.elements || []).map((item) => item.id === preview.id ? preview.element : item)
      : (draft.elements || []);
    const renderedLinks = preview && preview.type === 'move' && preview.link
      ? (draft.links || []).map((item) => item.id === preview.id ? preview.element : item)
      : (draft.links || []);

    return (
      <div className="screen-draft-canvas-shell">
        <svg
          ref={svgRef}
          className={'screen-draft-canvas tool-' + tool}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Screen draft canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ display: 'block', width: '100%', height: 'min(72vh, 760px)', minHeight: 420, background, border: '1px solid #d9dee8', borderRadius: 8, touchAction: 'none' }}
        >
          <Grid width={width} height={height} gridSize={gridSize} />
          <rect x="0" y="0" width={width} height={height} fill="transparent" />
          {renderedElements.map((item) => (
            <DraftElement key={item.id} element={item} selected={item.id === selectedElementId} />
          ))}
          {renderedLinks.map((item) => (
            <Hotspot key={item.id} link={item} selected={item.id === selectedElementId} />
          ))}
          {preview && preview.type === 'pen' ? <path d={pointsToPath(preview.points)} fill="none" stroke="#2856c4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity=".72" /> : null}
          {preview && preview.type === 'hotspot' ? <Hotspot link={{ id: 'preview', bounds: preview.bounds, label: 'Hotspot' }} preview /> : null}
        </svg>
      </div>
    );
  }

  function Grid({ width, height, gridSize }) {
    const lines = [];
    const major = gridSize * 4;
    for (let x = gridSize; x < width; x += gridSize) {
      lines.push(<line key={`x${x}`} x1={x} y1="0" x2={x} y2={height} stroke={x % major === 0 ? '#e4e8f0' : '#f0f2f6'} strokeWidth="1" />);
    }
    for (let y = gridSize; y < height; y += gridSize) {
      lines.push(<line key={`y${y}`} x1="0" y1={y} x2={width} y2={y} stroke={y % major === 0 ? '#e4e8f0' : '#f0f2f6'} strokeWidth="1" />);
    }
    return <g className="screen-draft-grid" aria-hidden="true">{lines}</g>;
  }

  function DraftElement({ element, selected }) {
    if (element.type === 'line') {
      return (
        <g className={'screen-draft-element' + (selected ? ' selected' : '')}>
          <line x1={element.x1} y1={element.y1} x2={element.x2} y2={element.y2} stroke={element.stroke || '#182033'} strokeWidth={element.width || 3} strokeLinecap="round" />
          {selected ? <SelectionBox bounds={elementBounds(element)} /> : null}
        </g>
      );
    }
    if (element.type === 'freehand') {
      return (
        <g className={'screen-draft-element' + (selected ? ' selected' : '')}>
          <path d={pointsToPath(element.points || [])} fill="none" stroke={element.stroke || '#182033'} strokeWidth={element.width || 3} strokeLinecap="round" strokeLinejoin="round" />
          {selected ? <SelectionBox bounds={elementBounds(element)} /> : null}
        </g>
      );
    }
    if (element.type === 'text') {
      const lines = String(element.text || '').split('\n');
      const fontSize = Number(element.fontSize) || 24;
      return (
        <g className={'screen-draft-element screen-draft-text' + (selected ? ' selected' : '')}>
          <text x={element.x} y={element.y} fill={element.color || '#182033'} fontSize={fontSize} fontFamily="Inter, ui-sans-serif, system-ui, sans-serif">
            {lines.map((line, index) => <tspan key={index} x={element.x} dy={index ? fontSize * 1.25 : 0}>{line}</tspan>)}
          </text>
          {selected ? <SelectionBox bounds={elementBounds(element)} /> : null}
        </g>
      );
    }
    return null;
  }

  function Hotspot({ link, selected, preview }) {
    const bounds = normalizeBounds(link.bounds || {});
    return (
      <g className={'screen-draft-hotspot' + (selected ? ' selected' : '') + (preview ? ' preview' : '')}>
        <rect x={bounds.x} y={bounds.y} width={bounds.width} height={bounds.height} rx="8" fill="rgba(40,86,196,.08)" stroke="#2856c4" strokeWidth="2" strokeDasharray={preview ? '8 6' : 'none'} />
        {link.label ? (
          <text x={bounds.x + 10} y={bounds.y + 22} fill="#2856c4" fontSize="16" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif">{link.label}</text>
        ) : null}
      </g>
    );
  }

  function SelectionBox({ bounds }) {
    const b = normalizeBounds(bounds);
    return <rect className="screen-draft-selection" x={b.x - 6} y={b.y - 6} width={b.width + 12} height={b.height + 12} rx="6" fill="none" stroke="#2856c4" strokeWidth="2" strokeDasharray="6 4" pointerEvents="none" />;
  }

  function parseDraft(record, project, active) {
    const json = recordJson(record);
    if (!json) return defaultScreenDraft(project, active);
    try {
      return normalizeDraft(JSON.parse(json), project, active, record);
    } catch (_) {
      return defaultScreenDraft(project, active);
    }
  }

  function parseDraftSnapshot(snapshot, project, active, record) {
    if (!snapshot) return defaultScreenDraft(project, active);
    try {
      return normalizeDraft(JSON.parse(snapshot), project, active, record);
    } catch (_) {
      return defaultScreenDraft(project, active);
    }
  }

  function defaultScreenDraft(project, active) {
    return normalizeDraft({
      type: 'appligator-screen-draft',
      version: 1,
      id: makeId('draft'),
      canvas: {
        width: 1440,
        height: 900,
        background: '#ffffff',
        gridSize: 24
      },
      elements: [],
      links: [],
      aiDigest: ''
    }, project, active);
  }

  function normalizeDraft(value, project, active, record) {
    const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const canvas = source.canvas && typeof source.canvas === 'object' && !Array.isArray(source.canvas) ? source.canvas : {};
    const sourceElements = Array.isArray(source.elements) ? source.elements : [];
    const elements = sourceElements.map(normalizeElement).filter(Boolean).slice(0, 400);
    const elementLinks = sourceElements.filter((item) => item && item.type === 'hotspot').map(normalizeLink).filter(Boolean);
    const sourceLinks = Array.isArray(source.links) ? source.links.map(normalizeLink).filter(Boolean) : [];
    const links = uniqueById(sourceLinks.concat(elementLinks)).slice(0, 120);
    return {
      type: 'appligator-screen-draft',
      version: 1,
      id: String(source.id || record && record.id || makeId('draft')),
      title: String(source.title || record && record.title || `${active && active.name || project && project.name || 'App'} screen`),
      canvas: {
        width: clampNumber(canvas.width, 320, 4000, 1440),
        height: clampNumber(canvas.height, 240, 4000, 900),
        background: String(canvas.background || '#ffffff').slice(0, 32),
        gridSize: clampNumber(canvas.gridSize, 8, 96, 24)
      },
      elements,
      links,
      aiDigest: source.aiDigest !== undefined ? source.aiDigest : ''
    };
  }

  function normalizeElement(item) {
    if (!item || typeof item !== 'object') return null;
    if (item.type === 'line') {
      return {
        id: String(item.id || makeId('line')),
        type: 'line',
        x1: round(item.x1),
        y1: round(item.y1),
        x2: round(item.x2),
        y2: round(item.y2),
        stroke: String(item.stroke || '#182033').slice(0, 32),
        width: clampNumber(item.width, 1, 16, 3)
      };
    }
    if (item.type === 'freehand') {
      const points = normalizePoints(item.points || []).slice(0, 160);
      if (points.length < 2) return null;
      return {
        id: String(item.id || makeId('path')),
        type: 'freehand',
        points,
        stroke: String(item.stroke || '#182033').slice(0, 32),
        width: clampNumber(item.width, 1, 16, 3)
      };
    }
    if (item.type === 'text') {
      const text = cleanText(item.text);
      if (!text) return null;
      return {
        id: String(item.id || makeId('txt')),
        type: 'text',
        x: round(item.x),
        y: round(item.y),
        text,
        fontSize: clampNumber(item.fontSize, 10, 72, 24),
        color: String(item.color || '#182033').slice(0, 32),
        width: clampNumber(item.width, 24, 1200, estimateTextWidth(text, item.fontSize || 24))
      };
    }
    return null;
  }

  function normalizeLink(item) {
    if (!item || typeof item !== 'object') return null;
    const bounds = normalizeBounds(item.bounds || item);
    if (bounds.width < 4 || bounds.height < 4) return null;
    return {
      id: String(item.id || makeId('hot')),
      type: 'hotspot',
      bounds,
      label: cleanText(item.label || 'Hotspot') || 'Hotspot',
      targetScreenDraftId: String(item.targetScreenDraftId || '')
    };
  }

  function linkToElement(link) {
    const bounds = normalizeBounds(link.bounds || {});
    return {
      id: link.id,
      type: 'hotspot',
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
      label: link.label || '',
      targetScreenDraftId: link.targetScreenDraftId || ''
    };
  }

  function stringifyDraft(value) {
    const draft = normalizeDraft(value);
    const hotspotElements = (draft.links || []).map(linkToElement);
    const compact = {
      type: draft.type,
      version: draft.version,
      canvas: draft.canvas,
      elements: draft.elements.concat(hotspotElements),
      links: draft.links
    };
    if (draft.aiDigest) compact.aiDigest = draft.aiDigest;
    return JSON.stringify(compact);
  }

  function pushHistorySnapshot(history, snapshot) {
    if (!snapshot) return history;
    if (history[history.length - 1] === snapshot) return history;
    return history.concat([snapshot]).slice(-HISTORY_LIMIT);
  }

  function isEditableTarget(target) {
    if (!target) return false;
    const tag = String(target.tagName || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || !!target.isContentEditable;
  }

  function draftBody(record, value, active, overrides = {}) {
    const draft = normalizeDraft(value);
    return {
      workPackageId: record && record.workPackageId || active && active.id || '',
      title: Object.prototype.hasOwnProperty.call(overrides, 'title') ? overrides.title || '' : record && record.title || draft.title || active && active.name || 'Screen draft',
      screenDraftJson: stringifyDraft(draft)
    };
  }

  function recordJson(record) {
    if (!record) return '';
    return record.screenDraftJson || record.draftJson || record.canvasJson || record.json || '';
  }

  function draftSignature(record) {
    return record ? `${record.updatedAt || ''}:${recordJson(record)}:${record.title || ''}` : '';
  }

  function draftMetaLabel(record) {
    const draft = parseDraft(record);
    const elementCount = draft.elements.length;
    const linkCount = draft.links.length;
    if (linkCount) return `${elementCount} elements, ${linkCount} hotspots`;
    return `${elementCount} elements`;
  }

  function normalizeStroke(points) {
    const normalized = normalizePoints(points);
    if (normalized.length < 2) return null;
    const first = normalized[0];
    const last = normalized[normalized.length - 1];
    if (distance(first, last) < 4) return null;
    if (isStraightish(normalized)) {
      return {
        id: makeId('line'),
        type: 'line',
        x1: round(first.x),
        y1: round(first.y),
        x2: round(last.x),
        y2: round(last.y),
        stroke: '#182033',
        width: 3
      };
    }
    const simplified = capPoints(rdp(normalized, 2.6), 120);
    if (simplified.length < 2) return null;
    return {
      id: makeId('path'),
      type: 'freehand',
      points: simplified.map((point) => [round(point.x), round(point.y)]),
      stroke: '#182033',
      width: 3
    };
  }

  function rdp(points, epsilon) {
    if (!points || points.length <= 2) return points || [];
    let maxDistance = 0;
    let index = 0;
    const first = points[0];
    const last = points[points.length - 1];
    for (let i = 1; i < points.length - 1; i += 1) {
      const d = perpendicularDistance(points[i], first, last);
      if (d > maxDistance) {
        index = i;
        maxDistance = d;
      }
    }
    if (maxDistance > epsilon) {
      const left = rdp(points.slice(0, index + 1), epsilon);
      const right = rdp(points.slice(index), epsilon);
      return left.slice(0, -1).concat(right);
    }
    return [first, last];
  }

  function capPoints(points, max) {
    if (!points || points.length <= max) return points || [];
    const capped = [];
    const last = points.length - 1;
    for (let i = 0; i < max; i += 1) {
      capped.push(points[Math.round((i / (max - 1)) * last)]);
    }
    return capped;
  }

  function isStraightish(points) {
    if (!points || points.length < 3) return true;
    const first = points[0];
    const last = points[points.length - 1];
    const length = distance(first, last);
    if (length < 18) return false;
    let maxDistance = 0;
    for (let i = 1; i < points.length - 1; i += 1) {
      maxDistance = Math.max(maxDistance, perpendicularDistance(points[i], first, last));
    }
    return maxDistance <= Math.max(6, length * 0.045);
  }

  function perpendicularDistance(point, start, end) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const denom = Math.sqrt(dx * dx + dy * dy);
    if (!denom) return distance(point, start);
    return Math.abs(dy * point.x - dx * point.y + end.x * start.y - end.y * start.x) / denom;
  }

  function hitTestDraft(draft, point) {
    const links = draft.links || [];
    for (let i = links.length - 1; i >= 0; i -= 1) {
      const link = links[i];
      if (pointInBounds(point, link.bounds)) return { id: link.id, element: link, link: true };
    }
    const elements = draft.elements || [];
    for (let i = elements.length - 1; i >= 0; i -= 1) {
      const element = elements[i];
      if (hitElement(element, point)) return { id: element.id, element, link: false };
    }
    return null;
  }

  function hitElement(element, point) {
    if (element.type === 'line') return distanceToSegment(point, { x: element.x1, y: element.y1 }, { x: element.x2, y: element.y2 }) <= Math.max(8, (element.width || 3) + 5);
    if (element.type === 'freehand') {
      const points = normalizePoints(element.points || []);
      for (let i = 1; i < points.length; i += 1) {
        if (distanceToSegment(point, points[i - 1], points[i]) <= Math.max(8, (element.width || 3) + 5)) return true;
      }
      return false;
    }
    if (element.type === 'text') return pointInBounds(point, elementBounds(element));
    return false;
  }

  function distanceToSegment(point, start, end) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) return distance(point, start);
    const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)));
    return distance(point, { x: start.x + t * dx, y: start.y + t * dy });
  }

  function replaceElementOrLink(draft, id, next) {
    const elements = (draft.elements || []).map((item) => item.id === id ? normalizeElement(next) || item : item);
    const links = (draft.links || []).map((item) => item.id === id ? normalizeLink(next) || item : item);
    return Object.assign({}, draft, { elements, links });
  }

  function removeElementOrLink(draft, id) {
    return Object.assign({}, draft, {
      elements: (draft.elements || []).filter((item) => item.id !== id),
      links: (draft.links || []).filter((item) => item.id !== id)
    });
  }

  function moveElement(element, dx, dy) {
    const next = cloneElement(element);
    if (next.type === 'line') {
      next.x1 = round(next.x1 + dx);
      next.y1 = round(next.y1 + dy);
      next.x2 = round(next.x2 + dx);
      next.y2 = round(next.y2 + dy);
    } else if (next.type === 'freehand') {
      next.points = normalizePoints(next.points || []).map((point) => [round(point.x + dx), round(point.y + dy)]);
    } else if (next.type === 'text') {
      next.x = round(next.x + dx);
      next.y = round(next.y + dy);
    } else if (next.type === 'hotspot') {
      const bounds = normalizeBounds(next.bounds || {});
      next.bounds = {
        x: round(bounds.x + dx),
        y: round(bounds.y + dy),
        width: bounds.width,
        height: bounds.height
      };
    }
    return next;
  }

  function elementBounds(element) {
    if (element.type === 'line') {
      return normalizeBounds(rectFromPoints({ x: element.x1, y: element.y1 }, { x: element.x2, y: element.y2 }));
    }
    if (element.type === 'freehand') {
      const points = normalizePoints(element.points || []);
      const xs = points.map((point) => point.x);
      const ys = points.map((point) => point.y);
      return normalizeBounds({ x: Math.min.apply(null, xs), y: Math.min.apply(null, ys), width: Math.max.apply(null, xs) - Math.min.apply(null, xs), height: Math.max.apply(null, ys) - Math.min.apply(null, ys) });
    }
    if (element.type === 'text') {
      const fontSize = Number(element.fontSize) || 24;
      const lines = String(element.text || '').split('\n');
      return normalizeBounds({
        x: Number(element.x) || 0,
        y: (Number(element.y) || 0) - fontSize,
        width: Number(element.width) || estimateTextWidth(element.text, fontSize),
        height: Math.max(fontSize, lines.length * fontSize * 1.25)
      });
    }
    if (element.type === 'hotspot') return normalizeBounds(element.bounds || {});
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  function pointInBounds(point, bounds) {
    const b = normalizeBounds(bounds);
    return point.x >= b.x && point.x <= b.x + b.width && point.y >= b.y && point.y <= b.y + b.height;
  }

  function eventPoint(event, svg) {
    if (!svg || !svg.createSVGPoint) return { x: 0, y: 0 };
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const matrix = svg.getScreenCTM();
    if (!matrix) return { x: 0, y: 0 };
    const local = point.matrixTransform(matrix.inverse());
    return { x: round(local.x), y: round(local.y) };
  }

  function normalizePoints(points) {
    return (points || []).map((point) => {
      if (Array.isArray(point)) return { x: Number(point[0]) || 0, y: Number(point[1]) || 0 };
      return { x: Number(point.x) || 0, y: Number(point.y) || 0 };
    }).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  }

  function pointsToPath(points) {
    const normalized = normalizePoints(points);
    if (!normalized.length) return '';
    return normalized.map((point, index) => `${index ? 'L' : 'M'}${round(point.x)} ${round(point.y)}`).join(' ');
  }

  function rectFromPoints(a, b) {
    return {
      x: Math.min(a.x, b.x),
      y: Math.min(a.y, b.y),
      width: Math.abs(a.x - b.x),
      height: Math.abs(a.y - b.y)
    };
  }

  function normalizeBounds(value) {
    return {
      x: round(value.x),
      y: round(value.y),
      width: Math.max(0, round(value.width)),
      height: Math.max(0, round(value.height))
    };
  }

  function distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function estimateTextWidth(text, fontSize) {
    const lines = String(text || '').split('\n');
    const longest = lines.reduce((max, line) => Math.max(max, line.length), 1);
    return Math.ceil(longest * (Number(fontSize) || 24) * 0.58);
  }

  function chooseTargetDraft(drafts, currentId) {
    const options = (drafts || []).filter((item) => item.id !== currentId);
    if (!options.length) return '';
    if (options.length === 1) return options[0].id;
    const labels = options.map((item, index) => `${index + 1}. ${item.title || 'Screen draft'}`).join('\n');
    const answer = window.prompt(`Target screen draft number:\n${labels}`, '1');
    const index = Math.max(0, Math.min(options.length - 1, (Number(answer) || 1) - 1));
    return options[index].id;
  }

  function cloneElement(element) {
    return JSON.parse(JSON.stringify(element || {}));
  }

  function cleanText(value) {
    return String(value || '').replace(/\r/g, '').trim().slice(0, 1000);
  }

  function clampNumber(value, min, max, fallback) {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.max(min, Math.min(max, number));
  }

  function round(value) {
    return Math.round((Number(value) || 0) * 10) / 10;
  }

  function makeId(prefix) {
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }

  function sortPositionTitle(a, b) {
    return (Number(a.position) || 0) - (Number(b.position) || 0)
      || String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''));
  }

  function uniqueById(items) {
    const seen = new Set();
    return (items || []).filter((item) => {
      if (!item || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  Object.assign(window, { ProjectScreenDraftPanel, defaultProjectScreenDraft: defaultScreenDraft });
})();
